import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { logoutUser } from '@/modules/Authentication';
import { useQuery } from '@tanstack/react-query';
import { queryClient } from '@/common/providers';
import { getCompanyIntegrations } from '../api';
import { messagingChannelsFromIntegrations } from '../utils/messagingChannels';
import {
  focusPageAfterSidebarClose,
  takeSidebarCloseFocusTarget,
} from '../utils/sidebarFocus';
import { useOneSignal } from '@/common/hooks/useOneSignal';
import { clearConversationDrafts } from '../whatsapp-chat/utils/conversationDrafts';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';
import {
  LayoutDashboard,
  FormInput,
  Users,
  Settings,
  MessageCircleIcon,
  ContactCardIcon,
  MegaphoneIcon,
  AutomationsIcon,
  MonitorIcon,
  BadgeIcon,
  NetworkNodesIcon,
  GroupsIcon,
  HexagonIcon,
  BlocksIcon,
  FeedbackIcon,
  QuotationIcon,
  Bell,
} from './Icons';
import { LogoutConfirmModal } from './LogoutConfirmModal';
import logo from '../assets/logo.png';
import dashboardIcon from '../assets/Sidebar/Dashboard.png';
import dashboardActiveIcon from '../assets/Sidebar/Dashboard_active.png';
import contactsIcon from '../assets/Sidebar/Contacts.png';
import contactsActiveIcon from '../assets/Sidebar/Contacts_active.png';
import templatesIcon from '../assets/Sidebar/Templates.png';
import templatesActiveIcon from '../assets/Sidebar/Templates_active.png';
import campaignsIcon from '../assets/Sidebar/campaing.png';
import campaignsActiveIcon from '../assets/Sidebar/campaing_active.png';
import inboxIcon from '../assets/Sidebar/Inbox.png';
import inboxActiveIcon from '../assets/Sidebar/Inbox_active.png';
import employeesIcon from '../assets/Sidebar/Employees.png';
import employeesActiveIcon from '../assets/Sidebar/Employees_active.png';
import automationsIcon from '../assets/Sidebar/Flow.png';
import automationsActiveIcon from '../assets/Sidebar/Flow_active.png';
import crmDashboardIcon from '../assets/Sidebar/monitor.png';
import crmDashboardActiveIcon from '../assets/Sidebar/monitor_active.png';
import leadsIcon from '../assets/Sidebar/Leads.png';
import leadsActiveIcon from '../assets/Sidebar/Leads_active.png';
import pipelineIcon from '../assets/Sidebar/Pipeline.png';
import pipelineActiveIcon from '../assets/Sidebar/Pipeline_active.png';
import segmentIcon from '../assets/Sidebar/Segment.png';
import segmentActiveIcon from '../assets/Sidebar/Segment_active.png';
import crmSettingsIcon from '../assets/Sidebar/CRM_Settings.png';
import crmSettingsActiveIcon from '../assets/Sidebar/CRM_Settings_active.png';
import integrationsIcon from '../assets/Sidebar/Integrations.png';
import integrationsActiveIcon from '../assets/Sidebar/Integrations_active.png';
import feedbackIcon from '../assets/Sidebar/Feedback.png';
import feedbackActiveIcon from '../assets/Sidebar/Feedback_active.png';
import calendarIcon from '../assets/Sidebar/calendar.png';
import calendarActiveIcon from '../assets/Sidebar/calendar_active.png';
import { useSelectedPlatform } from '../whatsapp-chat/state/selectedPlatformAtom';
import emailIcon from '../assets/Sidebar/sms.png';
import emailActiveIcon from '../assets/Sidebar/sms_active.png';
import whatsappIcon from '../assets/Sidebar/whatsapp.png';
import whatsappActiveIcon from '../assets/Sidebar/whatsapp_active.png';
import instagramIcon from '../assets/Sidebar/instagram.png';
import instagramActiveIcon from '../assets/Sidebar/instagram_active.png';

const iconMap = {
  LayoutDashboard,
  ContactCard: ContactCardIcon,
  FormInput,
  Megaphone: MegaphoneIcon,
  MessageCircle: MessageCircleIcon,
  Users,
  Automations: AutomationsIcon,
  Monitor: MonitorIcon,
  Badge: BadgeIcon,
  NetworkNodes: NetworkNodesIcon,
  Groups: GroupsIcon,
  Hexagon: HexagonIcon,
  Blocks: BlocksIcon,
  Settings,
  Feedback: FeedbackIcon,
  Quotation: QuotationIcon,
  Bell,
};

const navGroups = [
  {
    name: 'Home',
    items: [
      {
        name: 'Overview',
        imgSrc: dashboardIcon,
        imgSrcActive: dashboardActiveIcon,
        path: '/dashboard',
        permission: PERMISSIONS.DASHBOARD.VIEW,
        feature: 'dashboard',
      },
      {
        name: 'Contacts',
        imgSrc: contactsIcon,
        imgSrcActive: contactsActiveIcon,
        path: '/contacts',
        permission: PERMISSIONS.CONTACTS.VIEW,
        feature: 'contacts',
      },
      {
        name: 'Templates',
        imgSrc: templatesIcon,
        imgSrcActive: templatesActiveIcon,
        path: '/templates',
        permission: PERMISSIONS.WA_TEMPLATES.VIEW,
        feature: 'templates',
        subItems: [
          {
            name: 'WhatsApp',
            path: '/templates',
            id: 'whatsapp',
            imgSrc: whatsappIcon,
            imgSrcActive: whatsappActiveIcon,
          },
          {
            name: 'Email',
            path: '/email-templates',
            id: 'email',
            imgSrc: emailIcon,
            imgSrcActive: emailActiveIcon,
          },
        ],
      },
      {
        name: 'Campaigns',
        imgSrc: campaignsIcon,
        imgSrcActive: campaignsActiveIcon,
        path: '/campaigns',
        permission: PERMISSIONS.WA_CAMPAIGNS.VIEW,
        feature: 'campaign',
        subItems: [
          {
            name: 'WhatsApp',
            path: '/campaigns',
            id: 'whatsapp',
            imgSrc: whatsappIcon,
            imgSrcActive: whatsappActiveIcon,
          },
          {
            name: 'Email',
            path: '/email-campaigns',
            id: 'email',
            imgSrc: emailIcon,
            imgSrcActive: emailActiveIcon,
          },
        ],
      },
      {
        name: 'Inbox',
        imgSrc: inboxIcon,
        imgSrcActive: inboxActiveIcon,
        path: '/whatsapp-chat',
        permission: PERMISSIONS.WA_CHATS.VIEW,
        feature: 'inbox',
        subItems: [
          {
            name: 'WhatsApp',
            path: '/whatsapp-chat',
            id: 'whatsapp',
            imgSrc: whatsappIcon,
            imgSrcActive: whatsappActiveIcon,
          },
          {
            name: 'Instagram',
            path: '/whatsapp-chat',
            id: 'instagram',
            imgSrc: instagramIcon,
            imgSrcActive: instagramActiveIcon,
          },
          {
            name: 'Email',
            path: '/email-inbox',
            id: 'email',
            imgSrc: emailIcon,
            imgSrcActive: emailActiveIcon,
          },
        ],
      },
      {
        name: 'Employees',
        imgSrc: employeesIcon,
        imgSrcActive: employeesActiveIcon,
        path: '/employees',
        permission: PERMISSIONS.MEMBERS.VIEW,
        feature: 'users',
      },
      {
        name: 'Automations',
        imgSrc: automationsIcon,
        imgSrcActive: automationsActiveIcon,
        path: '/whatsapp-flows',
        permission: PERMISSIONS.WA_FLOWS.VIEW,
        feature: 'flows',
      },
      {
        name: 'Quotations',
        icon: 'Quotation',
        path: '/quotations',
        permission: PERMISSIONS.QUOTATIONS.VIEW,
        feature: 'quotations',
      },
      {
        name: 'Appointments',
        imgSrc: calendarIcon,
        imgSrcActive: calendarActiveIcon,
        path: '/calendar',
        permission: PERMISSIONS.APPOINTMENTS.VIEW,
        feature: 'appointments',
      },
    ],
  },
  {
    name: 'CRM',
    items: [
      {
        name: 'CRM Dashboard',
        imgSrc: crmDashboardIcon,
        imgSrcActive: crmDashboardActiveIcon,
        path: '/crm-dashboard',
        permission: PERMISSIONS.DASHBOARD.VIEW,
        feature: 'crm',
      },
      {
        name: 'Leads',
        imgSrc: leadsIcon,
        imgSrcActive: leadsActiveIcon,
        path: '/crm-contacts',
        permission: PERMISSIONS.LEADS.VIEW,
        feature: 'leads',
      },
      {
        name: 'Inquiries',
        imgSrc: pipelineIcon,
        imgSrcActive: pipelineActiveIcon,
        path: '/crm-pipeline',
        permission: PERMISSIONS.PIPELINES.VIEW,
        feature: 'inquires',
      },
      {
        name: 'Groups',
        imgSrc: segmentIcon,
        imgSrcActive: segmentActiveIcon,
        path: '/crm-segments',
        permission: PERMISSIONS.SEGMENTS.VIEW,
        feature: 'groups',
      },
      {
        name: 'Reminders',
        icon: 'Bell',
        path: '/reminders',
        permission: PERMISSIONS.REMINDERS.VIEW,
        feature: 'reminders',
      },
      {
        name: 'Configuration',
        imgSrc: crmSettingsIcon,
        imgSrcActive: crmSettingsActiveIcon,
        path: '/crm-settings',
        feature: 'configuration',
        permission: [
          PERMISSIONS.TAGS.VIEW,
          PERMISSIONS.CUSTOM_FIELDS.VIEW,
          PERMISSIONS.AUTO_ASSIGNMENT.VIEW,
        ],
      },
    ],
  },
  {
    name: 'General',
    items: [
      {
        name: 'Integrations',
        imgSrc: integrationsIcon,
        imgSrcActive: integrationsActiveIcon,
        path: '/integrations',
        permission: PERMISSIONS?.INTEGRATION?.VIEW || 'integration.view',
        feature: 'integrations',
      },
      {
        name: 'Feedback',
        imgSrc: feedbackIcon,
        imgSrcActive: feedbackActiveIcon,
        path: '/settings/feedback',
      },
    ],
  },
];

function isItemActive(currentPath, item, locationState) {
  if (currentPath === '/crm-contacts/overview') {
    const fromPath = locationState?.from;
    if (fromPath) {
      if (fromPath.startsWith('/crm-pipeline')) {
        return item.path === '/crm-pipeline';
      }
      if (fromPath.startsWith('/calendar')) {
        return item.path === '/calendar';
      }
      if (fromPath.startsWith('/crm-contacts')) {
        return item.path === '/crm-contacts';
      }
    }
  }

  if (item.matchPrefix) {
    return currentPath.startsWith(item.matchPrefix);
  }

  if (currentPath === item.path) {
    return true;
  }

  // Handle nested paths (e.g., /campaigns/123 under /campaigns, but avoid matching /dashboard with /dashboard-xyz)
  if (item.path !== '/' && item.path !== '/dashboard') {
    return currentPath.startsWith(`${item.path}/`);
  }

  return false;
}

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const SIDEBAR_COLLAPSE_KEY = 'aexon.sidebar.collapsed';
const COMPACT_LAPTOP_QUERY =
  '(min-width: 1024px) and (max-width: 1440px) and (max-height: 899px)';

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

function useIsDesktop() {
  const query = '(min-width: 1024px)';
  const [desktop, setDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : true,
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setDesktop(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return desktop;
}

function isHidden(el) {
  if (el.disabled || el.closest('[hidden], [inert]')) return true;
  const style = window.getComputedStyle(el);
  return style.display === 'none' || style.visibility === 'hidden';
}

function getFocusable(root) {
  if (!root) return [];
  return [...root.querySelectorAll(FOCUSABLE)].filter(
    (el) => el.tabIndex >= 0 && !isHidden(el),
  );
}

const getCompanyId = () => {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userObj = JSON.parse(storedUser);
      return userObj?.company_id || localStorage.getItem('company_id');
    }
  } catch {
    // Ignore error
  }
  return localStorage.getItem('company_id');
};

export default function Sidebar({
  isOpen,
  toggleSidebar,
  features,
  refreshFeatures,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  const { can, canAny } = usePermissions();
  const { logout: oneSignalLogout } = useOneSignal();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useSelectedPlatform();
  const isDesktop = useIsDesktop();
  const isCompactLaptop = useMediaQuery(COMPACT_LAPTOP_QUERY);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === '1',
  );
  const iconOnly = isCompactLaptop && collapsed;
  const [openFlyout, setOpenFlyout] = useState(null);
  const sidebarRef = useRef(null);
  const toggleRef = useRef(toggleSidebar);
  toggleRef.current = toggleSidebar;
  const mobileDrawer = !isDesktop && isOpen;

  useLayoutEffect(() => {
    const column = document.getElementById('dashboard-content');
    const el = sidebarRef.current;
    const hideFromKeyboard = !isDesktop && !isOpen;
    if (!mobileDrawer) column?.removeAttribute('inert');
    if (hideFromKeyboard) {
      const focusId = takeSidebarCloseFocusTarget();
      const toPage = focusId === 'page-heading';
      if (toPage || el?.contains(document.activeElement)) {
        document.getElementById(focusId)?.focus();
      }
    }
    if (el) {
      if (hideFromKeyboard) el.setAttribute('inert', '');
      else el.removeAttribute('inert');
    }
    if (mobileDrawer) column?.setAttribute('inert', '');
    return () => {
      column?.removeAttribute('inert');
    };
  }, [isDesktop, isOpen, mobileDrawer]);

  useEffect(() => {
    if (!mobileDrawer) return undefined;
    const panel = sidebarRef.current;
    getFocusable(panel)[0]?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        toggleRef.current?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = getFocusable(sidebarRef.current);
      if (!items.length) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const inside = sidebarRef.current?.contains(active);
      if (!inside || (e.shiftKey && active === first)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [mobileDrawer]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    setOpenFlyout(null);
  }, [currentPath, iconOnly]);

  useEffect(() => {
    if (!openFlyout) return undefined;
    const onDown = (event) => {
      if (!sidebarRef.current?.contains(event.target)) setOpenFlyout(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openFlyout]);

  useEffect(() => {
    const root = sidebarRef.current;
    if (!root || !iconOnly) return undefined;
    const place = (event) => {
      const control = event.target.closest?.('a, button');
      const tip = control?.querySelector('.sidebar-tip');
      if (!control || !tip) return;
      const rect = control.getBoundingClientRect();
      tip.style.left = `${rect.right + 8}px`;
      tip.style.top = `${rect.top + rect.height / 2 - 13}px`;
    };
    root.addEventListener('mouseover', place);
    root.addEventListener('focusin', place);
    return () => {
      root.removeEventListener('mouseover', place);
      root.removeEventListener('focusin', place);
    };
  }, [iconOnly]);

  const companyId = getCompanyId();
  const { data: companyIntegrations } = useQuery({
    queryKey: ['company-integrations', companyId],
    queryFn: () => getCompanyIntegrations(companyId),
    enabled: !!companyId,
  });

  const connectedMap = companyIntegrations
    ? messagingChannelsFromIntegrations(companyIntegrations)
    : { email: false, whatsapp: false, instagram: false };
  const connectedChannels = ['whatsapp', 'instagram', 'email'].filter(
    (id) => connectedMap[id],
  );

  const filteredNavGroups = navGroups
    .map((group) => {
      const filteredItems = group.items.filter((item) => {
        if (item.permission) {
          const allowed = Array.isArray(item.permission)
            ? canAny(item.permission)
            : can(item.permission);
          if (!allowed) return false;
        }
        if (item.feature && features !== null && !features?.[item.feature])
          return false;
        return true;
      });
      return { ...group, items: filteredItems };
    })
    .filter((group) => group.items.length > 0);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    const refreshToken = localStorage.getItem('refresh_token');
    try {
      if (refreshToken) {
        await logoutUser(refreshToken);
      }
    } catch (err) {
      console.error('Logout API error:', err);
    } finally {
      try {
        await Promise.race([
          oneSignalLogout(),
          new Promise((resolve) => setTimeout(resolve, 1000)),
        ]);
      } catch (err) {
        console.error('OneSignal logout error:', err);
      }
      queryClient.clear();
      clearConversationDrafts();
      localStorage.clear();
      toast.success('Logged out successfully');
      navigate(0);
    }
  };

  const NavItemWithSub = ({
    item,
    subItems,
    refreshFeatures,
    toggleSidebar,
    currentPath,
    selectedPlatform,
    setSelectedPlatform,
  }) => {
    const IconComponent = iconMap[item.icon];
    const isInbox = item.name === 'Inbox';

    const checkSubActive = (sub) => {
      if (isInbox) {
        if (sub.id === 'email') {
          return (
            currentPath.startsWith('/email-inbox') ||
            (currentPath.startsWith('/whatsapp-chat') &&
              selectedPlatform === 'email')
          );
        }
        return (
          currentPath.startsWith('/whatsapp-chat') &&
          selectedPlatform === sub.id
        );
      }
      return currentPath === sub.path || currentPath.startsWith(sub.path + '/');
    };

    const isSubItemActive = subItems.some(checkSubActive);
    const isHeaderActive =
      isSubItemActive || isItemActive(currentPath, item, location.state);

    const [isExpanded, setIsExpanded] = useState(isHeaderActive);
    const parentRef = useRef(null);
    const [flyoutPos, setFlyoutPos] = useState(null);

    useEffect(() => {
      if (isHeaderActive) {
        setIsExpanded(true);
      }
    }, [isHeaderActive]);

    const handleSubItemClick = (subItem) => {
      if (isInbox) {
        setSelectedPlatform(subItem.id);
        navigate(subItem.path || '/whatsapp-chat');
      } else {
        navigate(subItem.path);
      }
      if (refreshFeatures) refreshFeatures();
      if (window.innerWidth < 1024) {
        focusPageAfterSidebarClose();
        toggleSidebar();
      }
    };

    return (
      <li key={item.name} className="space-y-1">
        {/* Parent Header — using exact original UI design tokens */}
        <button
          ref={parentRef}
          type="button"
          aria-label={item.name}
          aria-expanded={iconOnly ? openFlyout === item.name : isExpanded}
          onClick={() => {
            if (iconOnly) {
              const rect = parentRef.current?.getBoundingClientRect();
              if (rect) setFlyoutPos({ top: rect.top, left: rect.right + 8 });
              setOpenFlyout((name) => (name === item.name ? null : item.name));
              return;
            }
            setIsExpanded((prev) => !prev);
          }}
          className={`relative w-full flex items-center justify-between py-2 laptop:!py-1.5 laptop:!min-h-9 pl-4 pr-3 rounded-[8px] text-[16px] leading-[22px] font-medium font-sf-pro-display transition-colors text-left cursor-pointer ${FOCUS_RING} ${
            isHeaderActive
              ? 'bg-surface-mint text-sidebar-active'
              : 'text-sidebar-default hover:bg-surface-mint/60'
          }`}
        >
          <div className="flex items-center gap-3">
            {isHeaderActive && (
              <span
                className="absolute left-0 top-2 bottom-2 w-[3px] bg-primary rounded-r-full"
                aria-hidden
              />
            )}
            {item.imgSrc ? (
              <img
                src={
                  isHeaderActive && item.imgSrcActive
                    ? item.imgSrcActive
                    : item.imgSrc
                }
                alt={item.name}
                className="w-5 h-5 shrink-0 object-contain"
              />
            ) : IconComponent ? (
              <IconComponent
                className={`w-5 h-5 shrink-0 ${
                  isHeaderActive
                    ? 'text-sidebar-active'
                    : 'text-sidebar-default'
                }`}
              />
            ) : null}
            <span className="sidebar-label">{item.name}</span>
            <span className="sidebar-tip" role="tooltip">
              {item.name}
            </span>
          </div>

          {/* Up / Down Chevron matching original theme */}
          <svg
            className={`sidebar-chevron w-4 h-4 transition-transform duration-200 ${
              isHeaderActive ? 'text-sidebar-active' : 'text-sidebar-default'
            } ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* Sub-items List (Type Selection) */}
        {iconOnly && openFlyout === item.name && (
          <div
            role="menu"
            className="sidebar-flyout"
            style={
              flyoutPos
                ? { top: flyoutPos.top, left: flyoutPos.left }
                : undefined
            }
          >
            {subItems.map((sub) => {
              const isSelected = checkSubActive(sub);
              return (
                <button
                  key={sub.name}
                  type="button"
                  role="menuitem"
                  onClick={() => handleSubItemClick(sub)}
                  className={`flex w-full items-center gap-2 rounded-[8px] px-3 py-2 text-left text-[14px] leading-5 ${FOCUS_RING} ${
                    isSelected
                      ? 'bg-surface-mint text-sidebar-active'
                      : 'text-sidebar-default hover:bg-surface-mint/60'
                  }`}
                >
                  {sub.name}
                </button>
              );
            })}
          </div>
        )}

        {!iconOnly && isExpanded && (
          <ul className="sidebar-sub pl-6 space-y-1 pt-0.5">
            {subItems.map((sub) => {
              const isSelected = checkSubActive(sub);
              const SubIconComponent = iconMap[sub.icon];

              return (
                <li key={sub.name}>
                  <button
                    type="button"
                    onClick={() => handleSubItemClick(sub)}
                    className={`relative w-full flex items-center justify-between py-2 laptop:!py-1.5 laptop:!min-h-9 pl-4 pr-3 rounded-[8px] text-[16px] leading-[22px] font-medium font-sf-pro-display transition-colors text-left cursor-pointer ${FOCUS_RING} ${
                      isSelected
                        ? 'bg-surface-mint text-sidebar-active'
                        : 'text-sidebar-default hover:bg-surface-mint/60'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {isSelected && (
                        <span
                          className="absolute left-0 top-2 bottom-2 w-[3px] bg-primary rounded-r-full"
                          aria-hidden
                        />
                      )}
                      {sub.imgSrc ? (
                        <img
                          src={
                            isSelected && sub.imgSrcActive
                              ? sub.imgSrcActive
                              : sub.imgSrc
                          }
                          alt={sub.name}
                          className="w-5 h-5 shrink-0 object-contain"
                        />
                      ) : SubIconComponent ? (
                        <SubIconComponent
                          className={`w-5 h-5 shrink-0 ${
                            isSelected
                              ? 'text-sidebar-active'
                              : 'text-sidebar-default'
                          }`}
                        />
                      ) : null}
                      <span className="sidebar-label">{sub.name}</span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  };

  const renderDirectItem = ({ item, targetPath, isActive, onClick }) => {
    const IconComponent = iconMap[item.icon];
    return (
      <li key={item.name}>
        <Link
          to={targetPath}
          onClick={() => {
            if (onClick) onClick();
            if (refreshFeatures) refreshFeatures();
            if (window.innerWidth < 1024) {
              focusPageAfterSidebarClose();
              toggleSidebar();
            }
          }}
          aria-label={item.name}
          className={`relative flex items-center justify-between py-2 laptop:!py-1.5 laptop:!min-h-9 pl-4 pr-3 rounded-[8px] text-[16px] leading-[22px] font-medium font-sf-pro-display transition-colors ${FOCUS_RING} ${
            isActive
              ? 'bg-surface-mint text-sidebar-active'
              : 'text-sidebar-default hover:bg-surface-mint/60'
          }`}
        >
          <div className="flex items-center gap-3">
            {isActive && (
              <span
                className="absolute left-0 top-2 bottom-2 w-[3px] bg-primary rounded-r-full"
                aria-hidden
              />
            )}
            {item.imgSrc ? (
              <img
                src={
                  isActive && item.imgSrcActive
                    ? item.imgSrcActive
                    : item.imgSrc
                }
                alt={item.name}
                className="w-5 h-5 shrink-0 object-contain"
              />
            ) : IconComponent ? (
              <IconComponent
                className={`w-5 h-5 shrink-0 ${
                  isActive ? 'text-sidebar-active' : 'text-sidebar-default'
                }`}
              />
            ) : null}
            <span className="sidebar-label">{item.name}</span>
            <span className="sidebar-tip" role="tooltip">
              {item.name}
            </span>
          </div>
        </Link>
      </li>
    );
  };

  const renderNavItem = (item) => {
    const isInbox = item.name === 'Inbox';
    const hasSubItems = item.subItems && item.subItems.length > 0;

    if (hasSubItems) {
      const connectedSubItems = item.subItems.filter((sub) =>
        connectedChannels.includes(sub.id),
      );

      // Case 1: More than 1 connected account -> show dropdown with type selection
      if (connectedSubItems.length > 1) {
        return (
          <NavItemWithSub
            key={item.name}
            item={item}
            subItems={connectedSubItems}
            refreshFeatures={refreshFeatures}
            toggleSidebar={toggleSidebar}
            currentPath={currentPath}
            selectedPlatform={selectedPlatform}
            setSelectedPlatform={setSelectedPlatform}
          />
        );
      }

      // Case 2: Exactly 1 connected account -> direct click to that account's path
      if (connectedSubItems.length === 1) {
        const singleSub = connectedSubItems[0];
        const targetPath =
          singleSub.path || (isInbox ? '/whatsapp-chat' : item.path);

        let isSingleActive = false;
        if (isInbox) {
          if (singleSub.id === 'email') {
            isSingleActive =
              currentPath.startsWith('/email-inbox') ||
              (currentPath.startsWith('/whatsapp-chat') &&
                selectedPlatform === 'email');
          } else {
            isSingleActive =
              currentPath.startsWith('/whatsapp-chat') &&
              selectedPlatform === singleSub.id;
          }
        } else {
          isSingleActive =
            currentPath === singleSub.path ||
            currentPath.startsWith(singleSub.path + '/') ||
            currentPath === item.path ||
            currentPath.startsWith(item.path + '/');
        }

        return renderDirectItem({
          item,
          targetPath,
          isActive: isSingleActive,
          onClick: () => {
            if (isInbox) setSelectedPlatform(singleSub.id);
          },
        });
      }

      // Case 3: 0 connected accounts (not connected) -> direct click to default related path
      return renderDirectItem({
        item,
        targetPath: item.path,
        isActive: isItemActive(currentPath, item, location.state),
        onClick: () => {
          if (isInbox) setSelectedPlatform('whatsapp');
        },
      });
    }

    const active = isItemActive(currentPath, item, location.state);
    return renderDirectItem({
      item,
      targetPath: item.path,
      isActive: active,
    });
  };

  return (
    <aside
      ref={sidebarRef}
      id="dashboard-sidebar"
      data-collapsed={iconOnly ? 'true' : 'false'}
      className={`fixed inset-y-0 left-0 z-50 lg:z-30 w-[260px] overflow-hidden lg:overflow-visible flex flex-col transition-[transform,width] duration-200 bg-white rounded-none lg:static lg:inset-auto lg:h-full lg:shrink-0 lg:transform-none ${
        isOpen ? 'translate-x-0' : '-translate-x-full lg:transform-none'
      }`}
      aria-label="Sidebar"
      aria-hidden={!isDesktop && !isOpen ? true : undefined}
    >
      <div className="sidebar-logo-row relative flex items-center justify-center shrink-0 px-4 pt-5 pb-5 laptop:!pt-2 laptop:!pb-2">
        <button
          type="button"
          className={`sidebar-collapse-toggle h-7 w-7 items-center justify-center rounded-[8px] text-sidebar-default hover:bg-surface-mint ${FOCUS_RING}`}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-pressed={collapsed}
          onClick={() => setCollapsed((value) => !value)}
        >
          {collapsed ? (
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <rect
                x="3"
                y="4"
                width="18"
                height="16"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path
                d="M9 4v16M13 9l3 3-3 3"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <rect
                x="3"
                y="4"
                width="18"
                height="16"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path
                d="M9 4v16M15 9l-3 3 3 3"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>
        <Link
          to="/dashboard"
          className={`flex items-center justify-center h-[60px] laptop:!h-11 w-[210px] cursor-pointer rounded-sm ${FOCUS_RING}`}
        >
          <img
            src={logo}
            alt="Logo"
            className="h-full w-full object-contain object-center"
          />
        </Link>
      </div>

      <div className="flex-1 min-h-0 px-3 pb-5 flex flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-4 laptop:!space-y-2">
          {filteredNavGroups.map((group) => (
            <div key={group.name}>
              <p className="sidebar-group-label px-3 mb-1.5 text-[16px] leading-[22px] font-medium font-sf-pro-display text-primary">
                {group.name}
              </p>
              <ul className="space-y-1 laptop:!space-y-0.5">
                {group.items.map(renderNavItem)}
              </ul>
            </div>
          ))}
        </div>

        {/* <div className="shrink-0 pt-4 px-1">
          <button
            type="button"
            onClick={() => setShowLogoutConfirm(true)}
            className="flex items-center justify-center gap-2.5 w-full py-2.5 px-4 rounded-button bg-primary text-white text-[16px] leading-[22px] font-medium font-sf-pro-display hover:bg-primary-hover transition-colors"
          >
            <img src={logoutIcon} alt="" className="w-5 h-5 object-contain" />
            <span>Log Out</span>
          </button>
        </div> */}
      </div>

      <LogoutConfirmModal
        isOpen={showLogoutConfirm}
        onCancel={() => {
          if (!isLoggingOut) setShowLogoutConfirm(false);
        }}
        onConfirm={handleLogout}
        isPending={isLoggingOut}
      />
    </aside>
  );
}
